<template>
  <div class="calendar-page">
    <div class="calendar-header">
      <button class="yr-btn" @click="changeYear(-1)" title="上一年 (←)">&lt;&lt;</button>
      <button class="yr-btn" @click="changeMonth(-1)" title="上一月 (Shift+←)">&lt;</button>
      <h2 class="calendar-year">{{ currentYear }}年</h2>
      <button class="yr-btn" @click="changeMonth(1)" title="下一月 (Shift+→)">&gt;</button>
      <button class="yr-btn" @click="changeYear(1)" title="下一年 (→)">&gt;&gt;</button>
      <span class="today-btn" @click="goToday">今天</span>
      <button class="print-btn" @click="printCalendar">🖨 打印</button>
    </div>

    <div class="months-grid">
      <div v-for="month in 12" :key="month" class="month-block">
        <h3 class="month-title">{{ month }}月</h3>
        <table class="cal-table">
          <thead>
            <tr>
              <th v-for="d in dayHeaders" :key="d" class="cal-th">{{ d }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, ri) in getMonthData(month)" :key="ri">
              <td
                v-for="(cell, ci) in row"
                :key="ci"
                class="cal-td"
                :class="{
                  'other-month': !cell.currentMonth,
                  'is-today': cell.isToday,
                  'is-start': cell.day && startDate && cell.dateStr === startDateStr,
                  'is-range': cell.day && isInRange(cell.dateStr)
                }"
                @click="cell.currentMonth && cell.day ? setStartDate(cell.dateObj, cell.dateStr) : null"
                @mouseenter="cell.currentMonth && cell.day && startDate ? hoverDate = cell.dateStr : null"
                @mouseleave="hoverDate = null"
              >
                <span v-if="cell.day" class="day-num">{{ cell.day }}</span>
                <span v-if="cell.day && cell.weekNum" class="week-num">{{ cell.weekNum }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="diff-info" v-if="startDate">
      <span>点击日期设为起算日 · 悬停查看日期差</span>
      <span v-if="diffDays !== null" class="diff-result">相差 <strong>{{ Math.abs(diffDays) }}</strong> 天</span>
    </div>

    <div class="year-links">
      <a v-for="y in yearRange" :key="y" href="javascript:void(0)"
         :class="{ active: y === currentYear }"
         @click="currentYear = y">{{ y }}</a>
    </div>

    <div class="kbd-hints">
      快捷键：← → 切换年份 | Shift+← → 切换月份 | Esc 回到今年
    </div>
  </div>
</template>

<script>
export default {
  name: 'CalendarPage',
  data() {
    return {
      currentYear: new Date().getFullYear(),
      dayHeaders: ['日', '一', '二', '三', '四', '五', '六'],
      startDate: null,
      startDateStr: '',
      hoverDate: null,
      yearRange: []
    }
  },
  computed: {
    diffDays() {
      if (!this.startDate || !this.hoverDate) return null
      const s = new Date(this.startDateStr)
      const e = new Date(this.hoverDate)
      return Math.round((e - s) / 86400000)
    }
  },
  created() {
    const y = this.currentYear
    this.yearRange = Array.from({ length: 9 }, (_, i) => y - 4 + i)
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'ArrowLeft') {
        if (e.shiftKey) { e.preventDefault(); this.changeMonth(-1) }
        else { e.preventDefault(); this.changeYear(-1) }
      } else if (e.key === 'ArrowRight') {
        if (e.shiftKey) { e.preventDefault(); this.changeMonth(1) }
        else { e.preventDefault(); this.changeYear(1) }
      } else if (e.key === 'Escape') {
        e.preventDefault(); this.goToday()
      }
    },
    changeYear(d) { this.currentYear += d; this.updateYearRange(); },
    changeMonth(d) {
      // Not needed for full year view — just scroll hint
      const el = document.querySelector('.month-block:nth-child(' + (Math.max(1, Math.min(12, new Date().getMonth() + 1 + d))) + ')')
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    goToday() {
      this.currentYear = new Date().getFullYear()
      this.startDate = null; this.startDateStr = ''; this.hoverDate = null
      this.updateYearRange()
    },
    printCalendar() {
      window.print()
    },
    updateYearRange() {
      const y = this.currentYear
      this.yearRange = Array.from({ length: 9 }, (_, i) => y - 4 + i)
    },
    getMonthData(month) {
      const year = this.currentYear
      const firstDay = new Date(year, month - 1, 1)
      const lastDay = new Date(year, month, 0)
      const daysInMonth = lastDay.getDate()
      const startDow = firstDay.getDay()

      const today = new Date()
      const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`

      // previous month tail
      const prevLastDay = new Date(year, month - 1, 0)
      const prevDays = prevLastDay.getDate()

      const rows = []
      let dayCount = 1
      let nextMonthDay = 1
      const totalCells = Math.ceil((startDow + daysInMonth) / 7) * 7

      for (let i = 0; i < totalCells; i++) {
        const row = Math.floor(i / 7)
        if (!rows[row]) rows[row] = []

        let day = null, currentMonth = false, dateObj = null, weekNum = null, isToday = false
        let dateStr = ''

        if (i < startDow) {
          // prev month
          day = prevDays - startDow + i + 1
          dateObj = new Date(year, month - 2, day)
        } else if (dayCount <= daysInMonth) {
          day = dayCount
          currentMonth = true
          dateObj = new Date(year, month - 1, dayCount)
          dateStr = `${year}-${String(month).padStart(2,'0')}-${String(dayCount).padStart(2,'0')}`
          if (dateStr === todayStr) isToday = true
          weekNum = this.getWeekNumber(dateObj)
          dayCount++
        } else {
          day = nextMonthDay
          dateObj = new Date(year, month, nextMonthDay)
          nextMonthDay++
        }
        rows[row].push({ day, currentMonth, dateObj, dateStr, weekNum, isToday })
      }
      return rows
    },
    getWeekNumber(date) {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
    },
    setStartDate(dateObj, dateStr) {
      this.startDate = dateObj
      this.startDateStr = dateStr
      this.hoverDate = null
    },
    isInRange(dateStr) {
      if (!this.startDate || !this.hoverDate || !dateStr) return false
      const s = new Date(this.startDateStr)
      const e = new Date(this.hoverDate)
      const d = new Date(dateStr)
      return (d >= s && d <= e) || (d >= e && d <= s)
    }
  }
}
</script>

<style scoped>
.calendar-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 16px 30px;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px 0 14px;
  flex-wrap: wrap;
}
.calendar-year {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  min-width: 120px;
  text-align: center;
}
.yr-btn {
  width: 36px; height: 36px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
  color: #555;
  transition: all 0.2s;
}
.yr-btn:hover { background: #1677ff; color: #fff; border-color: #1677ff; }
.today-btn {
  font-size: 13px; color: #1677ff; cursor: pointer; text-decoration: underline;
  margin-left: 8px;
}
.print-btn {
  padding: 6px 14px; font-size: 13px; border: 1px solid #1677ff;
  border-radius: 6px; background: #fff; color: #1677ff; cursor: pointer;
  margin-left: 8px; transition: all 0.2s;
}
.print-btn:hover { background: #1677ff; color: #fff; }

.months-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.month-block {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.05);
}
.month-title {
  font-size: 15px; font-weight: 700;
  color: #1a1a2e; text-align: center; margin-bottom: 8px;
}

.cal-table { width: 100%; border-collapse: collapse; }
.cal-th {
  font-size: 11px; color: #999; text-align: center;
  padding: 3px 0; font-weight: 500;
}
.cal-td {
  text-align: center; padding: 3px 1px;
  font-size: 12px; cursor: default;
  border-radius: 4px; position: relative; height: 36px; vertical-align: top;
}
.cal-td.other-month { color: #ccc; }
.cal-td.is-today { background: #1677ff; color: #fff; border-radius: 50%; }
.cal-td.is-today .week-num { color: #fff; }
.cal-td.is-start { background: #1677ff; color: #fff; border-radius: 8px 0 0 8px; }
.cal-td.is-range { background: #e6f4ff; }
.cal-td:not(.other-month):hover { background: #f0f5ff; cursor: pointer; }

.day-num { display: block; font-size: 13px; font-weight: 500; }
.week-num { display: block; font-size: 9px; color: #bbb; margin-top: 1px; }

.diff-info {
  text-align: center; margin-top: 16px;
  font-size: 13px; color: #888; display: flex; justify-content: center; gap: 20px;
  flex-wrap: wrap;
}
.diff-result strong { color: #1677ff; font-size: 18px; }

.year-links {
  display: flex; justify-content: center; gap: 10px; margin-top: 18px;
  flex-wrap: wrap;
}
.year-links a { font-size: 13px; color: #1677ff; text-decoration: none; }
.year-links a.active { font-weight: 700; color: #333; text-decoration: underline; }

.kbd-hints {
  text-align: center; font-size: 11px; color: #bbb; margin-top: 12px;
}

@media (max-width: 768px) {
  .months-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 480px) {
  .months-grid { grid-template-columns: repeat(1, 1fr); }
}

/* 打印样式 */
@media print {
  .calendar-header,
  .diff-info, .year-links, .kbd-hints { display: none; }
  .calendar-page { padding: 0; max-width: 100%; }
  .months-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .month-block { box-shadow: none; border: 1px solid #ddd; break-inside: avoid; page-break-inside: avoid; }
  body { background: #fff; }
}
</style>

<style>
/* 打印时隐藏全局头部和底部 */
@media print {
  .top-nav, .site-footer { display: none !important; }
}
</style>
